import { ApiError, NETWORK_ERROR } from './errors'
import { isUserActive } from '../auth/activity'
import { tokens } from '../auth/tokens'

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? ''

/** API kökü altındaki oturumsuz varlık adresi (ör. banka logosu <img src>). */
export const apiAssetUrl = (path: string) => (path ? BASE + path : '')

export type ApiResponse<T = unknown> = {
  success: boolean
  code: string
  message: string
  data?: T
  error?: unknown
  warning?: unknown
}

export type TokenPair = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  csrf_token?: string
  permissions?: string[]
  /** Yalnız "Bu cihazı hatırla" seçilen OTP doğrulamasında döner. */
  device_token?: string
}

export type Query = Record<string, string | number | boolean | undefined | null>

type Options = {
  body?: unknown
  query?: Query
  auth?: boolean
  signal?: AbortSignal
  raw?: boolean
  /** Yetki hatasında yenileme denenmez ve oturum-sonu yayınlanmaz (örn. zaten kapanmış oturumdan çıkış). */
  quietAuth?: boolean
}

const sessionListeners = new Set<(reason: string) => void>()
const passwordListeners = new Set<() => void>()

export function onSessionEnd(fn: (reason: string) => void) {
  sessionListeners.add(fn)
  return () => {
    sessionListeners.delete(fn)
  }
}

export function onMustChangePassword(fn: () => void) {
  passwordListeners.add(fn)
  return () => {
    passwordListeners.delete(fn)
  }
}

/** Oturumu yerelde sonlandırır (hareketsizlik vb.); dinleyiciler giriş ekranına döner. */
export function expireSession(reason: string) {
  endSession(reason)
}

function endSession(reason: string) {
  tokens.clear()
  sessionListeners.forEach((fn) => fn(reason))
}

function buildUrl(path: string, query?: Query) {
  const url = new URL(BASE + path, window.location.origin)
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  }
  return BASE ? url.toString() : url.pathname + url.search
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** Bazı 409’larda asıl `code` / `warning` dış zarfta değil `data` içinde gelir. */
function throwApiFailure(status: number, env: ApiResponse<unknown>) {
  const nested = asRecord(env.data)
  const nestedCode = typeof nested?.code === 'string' ? nested.code : ''
  const nestedMessage = typeof nested?.message === 'string' ? nested.message : ''
  const nestedWarning = nested?.warning
  const nestedLooksLikeError = Boolean(nestedCode || nestedWarning || nested?.success === false)

  throw new ApiError(
    status,
    (nestedLooksLikeError && nestedCode) || env.code || 'INTERNAL_ERROR',
    (nestedLooksLikeError && nestedMessage) || env.message || '',
    env.error ?? (nestedLooksLikeError ? undefined : env.data),
    nestedWarning ?? env.warning,
  )
}

async function readEnvelope<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  let env: ApiResponse<T> | null
  try {
    env = (await res.json()) as ApiResponse<T>
  } catch {
    throw new ApiError(res.status, 'INTERNAL_ERROR', '')
  }
  if (!env?.success || res.status === 409) {
    throwApiFailure(res.status, env as ApiResponse<unknown>)
  }
  return env.data as T
}

// Yenileme tek uçuşlu: aynı anda süresi dolan istekler tek bir /auth/refresh çağrısını bekler (yenileme belirteci her
// kullanımda döner; iki paralel yenileme ikincisini geçersiz kılardı).
let refreshing: Promise<boolean> | null = null

// Sekmeler yenileme belirtecini paylaşır ve belirteç her kullanımda döner: iki sekme aynı anda yenilerse ikincisi
// iptal edilmiş belirteçle reddedilir ve oturum düşerdi. Yenileme tarayıcı genelinde kilitle sıraya alınır; belirteç
// kilit içinde okunur, böylece başka sekmenin döndürdüğü en güncel belirteç kullanılır.
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  return locks ? locks.request('cp-auth-refresh', fn) : fn()
}

export function refreshSession(): Promise<boolean> {
  if (!tokens.refresh) return Promise.resolve(false)
  refreshing ??= withRefreshLock(async () => {
    const rt = tokens.refresh
    if (!rt) return false
    try {
      const res = await fetch(buildUrl('/api/v1/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      })
      const pair = await readEnvelope<TokenPair>(res)
      tokens.set(pair)
      return true
    } catch {
      return false
    }
  }).finally(() => {
    refreshing = null
  })
  return refreshing
}

const CSRF_CODES = new Set(['CSRF_INVALID', 'CSRF_ORIGIN_REJECTED'])
const REFRESH_CODES = new Set(['PERMISSIONS_CHANGED', 'TOKEN_EXPIRED'])
const LOGIN_CODES = new Set(['UNAUTHORIZED', 'SESSION_EXPIRED'])

async function refreshCsrf() {
  if (!tokens.access) return false
  try {
    const res = await fetch(buildUrl('/api/v1/auth/csrf'), {
      headers: { Accept: 'application/json', Authorization: `Bearer ${tokens.access}` },
    })
    const data = await readEnvelope<{ csrf_token: string }>(res)
    if (!data?.csrf_token) return false
    tokens.setCsrf(data.csrf_token)
    return true
  } catch {
    return false
  }
}

export async function request<T>(method: string, path: string, opts: Options = {}, retried = false): Promise<T> {
  const form = typeof FormData !== 'undefined' && opts.body instanceof FormData
  // Blob gövde (ör. büyük dosyanın parçası) olduğu gibi gönderilir.
  const binary = !form && typeof Blob !== 'undefined' && opts.body instanceof Blob
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined && !form) headers['Content-Type'] = binary ? 'application/octet-stream' : 'application/json'
  if (opts.auth !== false && tokens.access) headers.Authorization = `Bearer ${tokens.access}`
  if (tokens.csrf && method !== 'GET') headers['X-CSRF-Token'] = tokens.csrf
  // Kullanıcı etkin değilken giden istek (arka plan yoklaması) hareketsizlik süresini uzatmaz.
  if (opts.auth !== false && !isUserActive()) headers['X-Activity'] = 'passive'

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: opts.body === undefined ? undefined : form || binary ? (opts.body as BodyInit) : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    throw new ApiError(0, NETWORK_ERROR, 'Sunucuya ulaşılamadı')
  }

  if (opts.raw && res.ok) return res as unknown as T

  try {
    return await readEnvelope<T>(res)
  } catch (err) {
    if (!(err instanceof ApiError) || opts.auth === false || opts.quietAuth) throw err
    if (err.code === 'MUST_CHANGE_PASSWORD') {
      passwordListeners.forEach((fn) => fn())
      throw err
    }
    if (!retried && CSRF_CODES.has(err.code) && (await refreshCsrf())) {
      return request<T>(method, path, opts, true)
    }
    if (!retried && REFRESH_CODES.has(err.code) && (await refreshSession())) {
      return request<T>(method, path, opts, true)
    }
    if (LOGIN_CODES.has(err.code) || REFRESH_CODES.has(err.code)) endSession(err.code)
    throw err
  }
}

/** API adresinin tam URL'si (ör. oynatıcıya verilecek jetonlu akış bağlantısı). */
export const apiUrl = (path: string, query?: Query) => buildUrl(path, query).toString()

/** Dosya indirme: Authorization başlığı gerekir; bağlantı href ile açılmaz. */
export async function downloadFile(path: string, filename: string, query?: Query) {
  const res = await request<Response>('GET', path, { query, raw: true })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  // Sunucu PDF yerine taranmış görsel döndürebilir (örn. geçmiş bordro belgesi); uzantı içerik türüne uyarlanır.
  const imageExt = ({ 'image/jpeg': '.jpg', 'image/png': '.png', 'image/webp': '.webp' } as Record<string, string>)[blob.type]
  // Sunucu dosya adını verdiyse (Content-Disposition) o kullanılır.
  const served = /filename="?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
  a.download = served ?? (imageExt ? filename.replace(/\.pdf$/i, imageExt) : filename)
  a.click()
  URL.revokeObjectURL(url)
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body: body ?? {} }),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body }),
  public: <T>(method: string, path: string, body?: unknown) => request<T>(method, path, { body, auth: false }),
  download: downloadFile,
}
